import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { authorizedGet } from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import MessageBar, {
  config,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
} from "../../utils/helper";
import Spinner from "../../Components/Spinner/Spinner";

// Table layout: 4 category columns x (Pending | Accepted | Declined).
// Backend compares Admin Suggestion / Coordinator Suggestion / Request /
// Questionnaire / Marriage against each other by date and returns ONLY the
// single overall-latest one as `currentStatus` (with a `category` field
// telling us which group it belongs to). So out of all 12 cells in this
// table, exactly ONE gets filled -- every other cell stays empty ("—").
const GROUPS = [
  { key: "Admin Suggestion", title: "Admin Suggestion" },
  { key: "Coordinator Suggestion", title: "Coordinator Suggestion" },
  { key: "Request", title: "Request" },
  { key: "Questionnaire", title: "Questionnaire" },
];

const STATES = [
  { key: "pending", title: "Pending", head: "bg-yellow-100 text-yellow-800" },
  { key: "accepted", title: "Accepted", head: "bg-green-100 text-green-800" },
  { key: "declined", title: "Declined", head: "bg-red-100 text-red-800" },
];

const formatDate = (date) =>
  date ? new Date(date).toLocaleDateString("en-GB") : "";

const EntryCell = ({ entry, pageNo }) => (
  <div className="flex flex-col gap-0.5">
    <Link
      to={`/matrimony/${pageNo}/viewProfile/${entry.partnerId}`}
      className="font-semibold text-blue-600 hover:underline"
    >
      {entry.partnerName}
    </Link>
    {entry.note && <span className="text-[11px] text-gray-500">{entry.note}</span>}
    <div className="flex flex-wrap items-center gap-1">
      {entry.date && (
        <span className="text-[10px] text-gray-400">
          Sent: {formatDate(entry.date)}
        </span>
      )}
      {entry.inTalk && (
        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
          In Talk
        </span>
      )}
      {entry.suggestedBy && (
        <span className="text-[10px] text-gray-400">By: {entry.suggestedBy}</span>
      )}
    </div>
  </div>
);

const UserStatusDetails = () => {
  const { userId, pageNo } = useParams();
  const navigate = useNavigate();
  const role = getValueFromLocalStorage(localStorageKey.USERDATA, "role");
  const isAdminLike =
    role === "admin" || role === "superAdmin" || role === "coordinator";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAdminLike) {
      setLoading(false);
      return;
    }
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const res = await authorizedGet(
          `${configuration.apis.userStatusDetails}?userId=${userId}`,
          config,
        );
        if (res?.data?.status) {
          setData(res.data.data);
        } else {
          errorNotify(res?.data?.message || "Could not load status details.");
        }
      } catch (err) {
        errorNotify(
          err?.response?.data?.message || "Could not load status details.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [userId]);

  if (!isAdminLike) {
    return (
      <div className="font-jost p-6 text-center text-red-600">
        You do not have access to this page.
      </div>
    );
  }

  const status = data?.currentStatus;
  // "Marriage" has no column in the 4-group table -- show it as a banner
  // above the table instead when it's the overall-latest status.
  const isMarriage = status?.category === "Marriage";
  const tableStatus = isMarriage ? null : status;

  // Build the 12 columns; only the ONE column matching currentStatus's
  // category + state gets the entry, everything else stays empty.
  const columns = GROUPS.flatMap((g) =>
    STATES.map((s) => {
      const matches =
        tableStatus &&
        tableStatus.category === g.key &&
        tableStatus.state === s.key;
      return {
        group: g.key,
        state: s.key,
        entry: matches ? tableStatus : null,
      };
    }),
  );

  return (
    <div className="font-jost flex w-full flex-col p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-textGreen md:text-2xl">
          Current Status{data?.user?.name ? ` - ${data.user.name}` : ""}
        </h1>
        <p
          onClick={() => navigate(-1)}
          className="cursor-pointer text-sm text-blue-600 underline md:text-base"
        >
          Back
        </p>
      </div>

      {loading ? (
        <div className="flex h-[50vh] w-full items-center justify-center">
          <Spinner />
        </div>
      ) : !status ? (
        <div className="rounded-lg bg-white p-6 text-center text-gray-500 shadow-sm">
          No suggestions, requests or questionnaires yet. This user is
          currently free.
        </div>
      ) : isMarriage ? (
        <div className="max-w-md rounded-xl border border-green-300 bg-green-50 p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-green-700">
            Marriage
          </p>
          <div className="mt-2 text-sm text-green-800">
            💍 {status.note || "Married"} with{" "}
            <Link
              to={`/matrimony/${pageNo}/viewProfile/${status.partnerId}`}
              className="font-semibold text-blue-600 hover:underline"
            >
              {status.partnerName}
            </Link>
          </div>
          {status.date && (
            <p className="mt-2 text-xs text-gray-400">
              {formatDate(status.date)}
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full min-w-[1100px] table-fixed border-collapse text-sm">
            <thead className="sticky top-0 z-10">
              <tr>
                {GROUPS.map((g, idx) => (
                  <th
                    key={g.key}
                    colSpan={3}
                    className={`border-b border-gray-200 bg-gray-50 px-2 py-2.5 text-center text-[13px] font-semibold uppercase tracking-wide text-gray-700 ${
                      idx > 0 ? "border-l-4 border-l-gray-300" : ""
                    }`}
                  >
                    {g.title}
                  </th>
                ))}
              </tr>
              <tr>
                {columns.map((c, idx) => {
                  const state = STATES.find((s) => s.key === c.state);
                  const isGroupStart = idx % STATES.length === 0;
                  return (
                    <th
                      key={`${c.group}-${c.state}`}
                      className={`border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold ${state.head} ${
                        isGroupStart && idx > 0 ? "border-l-4 border-l-gray-300" : ""
                      }`}
                    >
                      {state.title}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {columns.map((c, idx) => {
                  const isGroupStart = idx % STATES.length === 0;
                  return (
                    <td
                      key={`${c.group}-${c.state}`}
                      className={`border-b border-gray-100 px-2 py-2.5 align-top ${
                        isGroupStart && idx > 0
                          ? "border-l-4 border-l-gray-300"
                          : ""
                      }`}
                    >
                      {c.entry ? (
                        <EntryCell entry={c.entry} pageNo={pageNo} />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <MessageBar />
    </div>
  );
};

export default UserStatusDetails;